import React from 'react';
import Input from '../common/Input';
import { autoHyphenPhone, todayISO } from './obituaryUtils';

const MOURNER_RELATIONSHIPS = [
  '장남', '차남', '장녀', '차녀', '며느리', '사위',
  '손자', '손녀', '남편', '아내', '형제', '자매', '기타',
];

/**
 * ObituaryForm - 부고 작성 공통 폼 컴포넌트
 * 단계별 부고 작성 페이지에서 재사용
 */
const ObituaryForm = ({
  formData,
  onChange,
  errors = {},
  step = 'all' // 'deceased' | 'funeral' | 'mourner' | 'all'
}) => {
  const today = todayISO();

  const handleChange = (e) => {
    const { name, value } = e.target;
    // 상주 연락처는 입력 중 자동 하이픈
    const nextValue = name === 'chiefMournerPhone' ? autoHyphenPhone(value) : value;
    onChange({ ...formData, [name]: nextValue });
  };

  const handleMournerChange = (index, field, value) => {
    const updatedMourners = [...(formData.additionalMourners || [])];
    updatedMourners[index] = {
      ...updatedMourners[index],
      [field]: field === 'phone' ? autoHyphenPhone(value) : value
    };
    onChange({ ...formData, additionalMourners: updatedMourners });
  };

  const addMourner = () => {
    const updatedMourners = [
      ...(formData.additionalMourners || []),
      { name: '', relationship: '', phone: '' }
    ];
    onChange({ ...formData, additionalMourners: updatedMourners });
  };

  const removeMourner = (index) => {
    const updatedMourners = (formData.additionalMourners || []).filter(
      (_, i) => i !== index
    );
    onChange({ ...formData, additionalMourners: updatedMourners });
  };

  return (
    <div className="space-y-6">
      {/* 1단계: 고인 정보 */}
      {(step === 'all' || step === 'deceased') && (
        <section className="space-y-4">
          <h3 className="text-lg font-bold text-primary-900 border-b pb-2">
            고인 정보
          </h3>

          <Input
            label="고인명"
            name="deceasedName"
            value={formData.deceasedName || ''}
            onChange={handleChange}
            placeholder="예: 김영수"
            required
            error={errors.deceasedName}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label htmlFor="deceasedBirthDate" className="block text-sm font-medium text-primary mb-1">
                생년월일<span className="text-red-600 ml-1">*</span>
              </label>
              <input
                type="date"
                name="deceasedBirthDate"
                id="deceasedBirthDate"
                value={formData.deceasedBirthDate || ''}
                onChange={handleChange}
                max={today}
                required
                className={`w-full px-4 py-2 rounded-lg border bg-white text-primary focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.deceasedBirthDate ? 'border-red-500 focus:ring-red-500' : 'border-accent-300 focus:ring-primary-500'}`}
              />
              {errors.deceasedBirthDate && (
                <p className="mt-1 text-sm text-red-600">{errors.deceasedBirthDate}</p>
              )}
            </div>

            <div className="w-full">
              <label htmlFor="deceasedDeathDate" className="block text-sm font-medium text-primary mb-1">
                사망일<span className="text-red-600 ml-1">*</span>
              </label>
              <input
                type="date"
                name="deceasedDeathDate"
                id="deceasedDeathDate"
                value={formData.deceasedDeathDate || ''}
                onChange={handleChange}
                max={today}
                required
                className={`w-full px-4 py-2 rounded-lg border bg-white text-primary focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${errors.deceasedDeathDate ? 'border-red-500 focus:ring-red-500' : 'border-accent-300 focus:ring-primary-500'}`}
              />
              {errors.deceasedDeathDate && (
                <p className="mt-1 text-sm text-red-600">{errors.deceasedDeathDate}</p>
              )}
            </div>
          </div>

          <div className="w-full">
            <label htmlFor="relationship" className="block text-sm font-medium text-primary mb-1">
              관계 <span className="text-red-600 ml-1">*</span>
            </label>
            <select
              name="relationship"
              id="relationship"
              value={formData.relationship || ''}
              onChange={handleChange}
              required
              className={`
                w-full px-4 py-2 rounded-lg border
                ${errors.relationship ? 'border-red-500 focus:ring-red-500' : 'border-accent-300 focus:ring-primary-500'}
                bg-white text-primary
                focus:outline-none focus:ring-2 focus:border-transparent
                transition-all duration-200
              `}
            >
              <option value="">선택하세요</option>
              <option value="부친">부친</option>
              <option value="모친">모친</option>
              <option value="조부">조부</option>
              <option value="조모">조모</option>
              <option value="배우자">배우자</option>
              <option value="형제">형제</option>
              <option value="자매">자매</option>
              <option value="자녀">자녀</option>
              <option value="기타">기타</option>
            </select>
            {errors.relationship && (
              <p className="mt-1 text-sm text-red-600">{errors.relationship}</p>
            )}
          </div>
        </section>
      )}

      {/* 2단계: 장례식장 정보 */}
      {(step === 'all' || step === 'funeral') && (
        <section className="space-y-4">
          <h3 className="text-lg font-bold text-primary-900 border-b pb-2">
            장례식장 정보
          </h3>

          <Input
            label="장례식장명"
            name="funeralHome"
            value={formData.funeralHome || ''}
            onChange={handleChange}
            placeholder="예: 서울대학교병원 장례식장"
            required
            error={errors.funeralHome}
          />

          <Input
            label="장례식장 주소"
            name="funeralAddress"
            value={formData.funeralAddress || ''}
            onChange={handleChange}
            placeholder="예: 서울특별시 종로구 대학로 101"
            required
            error={errors.funeralAddress}
          />

          <Input
            label="호실"
            name="funeralRoom"
            value={formData.funeralRoom || ''}
            onChange={handleChange}
            placeholder="예: 3호실"
            required
            error={errors.funeralRoom}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="발인일"
              name="departureDate"
              value={formData.departureDate || ''}
              onChange={handleChange}
              required
              error={errors.departureDate}
            />

            <Input
              type="time"
              label="발인시간"
              name="departureTime"
              value={formData.departureTime || ''}
              onChange={handleChange}
              required
              error={errors.departureTime}
            />
          </div>
        </section>
      )}

      {/* 3단계: 상주 정보 */}
      {(step === 'all' || step === 'mourner') && (
        <section className="space-y-4">
          <h3 className="text-lg font-bold text-primary-900 border-b pb-2">
            상주 정보
          </h3>

          <Input
            label="상주명"
            name="chiefMourner"
            value={formData.chiefMourner || ''}
            onChange={handleChange}
            placeholder="예: 김철수"
            required
            error={errors.chiefMourner}
          />

          <Input
            label="상주 연락처"
            name="chiefMournerPhone"
            type="tel"
            value={formData.chiefMournerPhone || ''}
            onChange={handleChange}
            placeholder="예: 010-1234-5678"
            required
            error={errors.chiefMournerPhone}
          />

          <Input
            label="상주 관계"
            name="chiefMournerRelationship"
            value={formData.chiefMournerRelationship || ''}
            onChange={handleChange}
            placeholder="예: 장남"
            required
            error={errors.chiefMournerRelationship}
          />

          {/* 추가 상주 */}
          <div className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <h4 className="text-md font-semibold text-primary-900">
                추가 상주 (선택)
              </h4>
              <button
                type="button"
                onClick={addMourner}
                className="px-3 py-1 text-sm bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                + 추가
              </button>
            </div>

            {(formData.additionalMourners || []).map((mourner, index) => (
              <div key={index} className="flex flex-wrap gap-2 items-start">
                <Input
                  placeholder="이름"
                  value={mourner.name || ''}
                  onChange={(e) => handleMournerChange(index, 'name', e.target.value)}
                  className="flex-1 min-w-[6rem]"
                />
                <div className="flex-1 min-w-[6rem]">
                  <select
                    value={mourner.relationship || ''}
                    onChange={(e) => handleMournerChange(index, 'relationship', e.target.value)}
                    aria-label="추가 상주 관계"
                    className="w-full px-4 py-2 rounded-lg border border-accent-300 bg-white text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">관계 선택</option>
                    {MOURNER_RELATIONSHIPS.map((rel) => (
                      <option key={rel} value={rel}>{rel}</option>
                    ))}
                  </select>
                </div>
                <Input
                  type="tel"
                  placeholder="연락처(선택)"
                  value={mourner.phone || ''}
                  onChange={(e) => handleMournerChange(index, 'phone', e.target.value)}
                  className="flex-1 min-w-[8rem]"
                />
                <button
                  type="button"
                  onClick={() => removeMourner(index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  aria-label="삭제"
                >
                  삭제
                </button>
              </div>
            ))}
          </div>

          {/* 추가 안내 정보 (선택) */}
          <div className="space-y-4 mt-6 border-t pt-4">
            <h4 className="text-md font-semibold text-primary-900">
              추가 안내 (선택)
            </h4>

            <Input
              label="영정 사진 URL"
              name="portraitUrl"
              value={formData.portraitUrl || ''}
              onChange={handleChange}
              placeholder="https://..."
            />

            <Input
              label="부의금 계좌"
              name="donationAccount"
              value={formData.donationAccount || ''}
              onChange={handleChange}
              placeholder="예: 국민은행 123456-78-901234 (예금주 홍길동)"
            />
          </div>
        </section>
      )}
    </div>
  );
};

export default ObituaryForm;
